/**
 * Collect filled closed geometry from a mounted path SVG and test points
 * for fill hit-testing (Object fill pack tool).
 */

const FILL_PACK_SKIP_TAGS = new Set([
    "defs",
    "clippath",
    "mask",
    "pattern",
    "symbol",
    "marker",
    "title",
    "desc",
    "metadata",
    "style",
    "script",
]);

export function hasPaintedFill(el: SVGGeometryElement): boolean {
    const cs = getComputedStyle(el);
    const fill = cs.fill;
    if (fill === "none") return false;
    if (fill === "transparent") return false;
    const m = /^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)/.exec(
        fill,
    );
    if (m && Number.parseFloat(m[1]) === 0) return false;
    return true;
}

function hasExplicitFillDeclaration(el: Element): boolean {
    let node: Element | null = el;
    while (node) {
        if (node.hasAttribute("fill")) return true;
        const styleAttr = node.getAttribute("style");
        if (styleAttr && /(?:^|;)\s*fill\s*:/.test(styleAttr)) return true;
        if (node.tagName.toLowerCase() === "svg") break;
        node = node.parentElement;
    }
    return false;
}

export function isClosedFillableGeometry(el: SVGGeometryElement): boolean {
    if (el instanceof SVGPathElement) {
        const d = el.getAttribute("d")?.trim() ?? "";
        return d.length > 0 && /z\s*$/i.test(d);
    }
    if (el instanceof SVGRectElement) {
        const w = el.width.baseVal.value;
        const h = el.height.baseVal.value;
        return w > 0 && h > 0 && Number.isFinite(w) && Number.isFinite(h);
    }
    if (el instanceof SVGCircleElement) {
        return el.r.baseVal.value > 0;
    }
    if (el instanceof SVGEllipseElement) {
        return el.rx.baseVal.value > 0 && el.ry.baseVal.value > 0;
    }
    if (el instanceof SVGPolygonElement) {
        return Boolean(el.getAttribute("points")?.trim());
    }
    return false;
}

export interface FillPackTarget {
    el: SVGGeometryElement;
    /** Stable index among collected targets (for coloring / hashing). */
    regionIndex: number;
}

function walkCollect(
    node: Element,
    out: SVGGeometryElement[],
): void {
    const tag = node.tagName.toLowerCase();
    if (FILL_PACK_SKIP_TAGS.has(tag)) {
        return;
    }

    if (node instanceof SVGGeometryElement) {
        if (isClosedFillableGeometry(node) && hasPaintedFill(node)) {
            out.push(node);
        }
    }

    for (let i = 0; i < node.children.length; i++) {
        walkCollect(node.children[i], out);
    }
}

export function collectFillPackTargets(root: SVGSVGElement): FillPackTarget[] {
    const els: SVGGeometryElement[] = [];
    walkCollect(root, els);
    return els.map((el, regionIndex) => ({ el, regionIndex }));
}

export interface CollectFillPackTargetOptions {
    explicitFillOnly?: boolean;
}

export function collectFillPackTargetsWithOptions(
    root: SVGSVGElement,
    options: CollectFillPackTargetOptions = {},
): FillPackTarget[] {
    const all = collectFillPackTargets(root);
    if (!options.explicitFillOnly) return all;
    const filtered = all.filter(({ el }) => hasExplicitFillDeclaration(el));
    return filtered.map((item, regionIndex) => ({
        el: item.el,
        regionIndex,
    }));
}

/**
 * True if `(x, y)` lies in the painted fill of `el` (root SVG user space).
 * Uses `SVGGeometryElement.isPointInFill` when available.
 */
export function isPointInsideGeometryFill(
    svg: SVGSVGElement,
    el: SVGGeometryElement,
    x: number,
    y: number,
): boolean {
    const isPointInFill = el.isPointInFill;
    if (typeof isPointInFill !== "function") {
        return false;
    }
    const pt = svg.createSVGPoint();
    pt.x = x;
    pt.y = y;
    return isPointInFill.call(el, pt);
}

export interface GridCandidate {
    x: number;
    y: number;
    regionIndex: number;
}

/**
 * Grid of cell centers inside an inset bbox (same layout as iteration order: j then i).
 */
export interface BBoxGridSpec {
    startX: number;
    startY: number;
    nx: number;
    ny: number;
}

export function getBBoxGridSpec(
    bbox: DOMRect,
    spacing: number,
    inset: number,
): BBoxGridSpec | null {
    if (spacing <= 0 || !Number.isFinite(spacing)) return null;
    const minX = bbox.x + inset;
    const minY = bbox.y + inset;
    const maxX = bbox.x + bbox.width - inset;
    const maxY = bbox.y + bbox.height - inset;
    if (maxX <= minX || maxY <= minY) return null;

    const startX = minX + spacing / 2;
    const startY = minY + spacing / 2;
    if (startX > maxX || startY > maxY) return null;

    const nx = Math.max(0, Math.floor((maxX - startX) / spacing + 1e-9) + 1);
    const ny = Math.max(0, Math.floor((maxY - startY) / spacing + 1e-9) + 1);
    if (nx === 0 || ny === 0) return null;
    return { startX, startY, nx, ny };
}

export function* iterateGridCentersInBBox(
    bbox: DOMRect,
    spacing: number,
    inset: number,
): Generator<{ x: number; y: number }, void, unknown> {
    const spec = getBBoxGridSpec(bbox, spacing, inset);
    if (!spec) return;
    const { startX, startY, nx, ny } = spec;
    for (let j = 0; j < ny; j++) {
        const y = startY + j * spacing;
        for (let i = 0; i < nx; i++) {
            const x = startX + i * spacing;
            yield { x, y };
        }
    }
}

/**
 * Clone the path SVG's direct subtree with fills removed and a uniform stroke
 * for guide / export underlay (DOMParser in browser).
 */
export function serializeSvgInnerAsStrokeGuides(
    pathSvgFullMarkup: string,
    stroke: string,
    strokeWidth: number,
): string {
    if (typeof DOMParser === "undefined") return "";
    const doc = new DOMParser().parseFromString(
        pathSvgFullMarkup,
        "image/svg+xml",
    );
    const root = doc.documentElement;
    if (!(root instanceof SVGSVGElement)) return "";
    if (root.tagName.toLowerCase() === "parsererror") return "";

    function walk(node: Element): void {
        const tag = node.tagName.toLowerCase();
        if (FILL_PACK_SKIP_TAGS.has(tag)) return;
        if (node instanceof SVGGeometryElement) {
            node.setAttribute("fill", "none");
            node.setAttribute("stroke", stroke);
            node.setAttribute("stroke-width", String(strokeWidth));
        }
        for (let i = 0; i < node.children.length; i++) {
            walk(node.children[i]);
        }
    }

    for (let i = 0; i < root.children.length; i++) {
        walk(root.children[i]);
    }

    return Array.from(root.children)
        .map((c) => (c as Element).outerHTML)
        .join("\n");
}
